/**
 * One traced play on this device (ADR 0035, P3-OBS-05): how the game ran, second by second, for the
 * trace the host uploads when the play ends. Numbers and named marks only: no person, nothing
 * typed. Fed by the host's display loop (frame times) and once a second (network, memory, long
 * tasks); kept in memory until the upload.
 */

/** At most this many seconds are kept (an hour); later seconds still count in the summary. */
export const MAX_SECONDS = 3600;
const MAX_MARKS = 500;

const percentile = (sorted: readonly number[], p: number) =>
  sorted.length === 0
    ? null
    : (sorted[Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1)] ?? null);
const round1 = (n: number | null) => (n === null ? null : Math.round(n * 10) / 10);

export type Numbers = Record<string, number | null>;

export class Tracer {
  readonly #start: number;
  #secondFrames: number[] = [];
  readonly #allFrames: number[] = [];
  readonly #timeline: Numbers[] = [];
  readonly #marks: { t: number; what: string }[] = [];
  #seconds = 0;
  #under20 = 0;
  #under30 = 0;
  /** The latest of each value sampled once a second, and the largest seen. */
  readonly #latest = new Map<string, number>();
  readonly #largest = new Map<string, number>();

  constructor(start: number) {
    this.#start = start;
  }

  /** One display frame took `ms` since the one before. */
  frame(ms: number): void {
    if (!Number.isFinite(ms) || ms <= 0 || ms > 10_000) return;
    this.#secondFrames.push(ms);
  }

  /** A second has passed: its frames, plus what the host sampled (network, memory, long tasks). */
  second(now: number, sampled: Numbers): void {
    const sorted = [...this.#secondFrames].sort((a, b) => a - b);
    this.#allFrames.push(...sorted);
    const frames = sorted.length;
    this.#seconds++;
    if (frames < 20) this.#under20++;
    if (frames < 30) this.#under30++;
    for (const [k, v] of Object.entries(sampled))
      if (typeof v === "number" && Number.isFinite(v)) {
        this.#latest.set(k, v);
        this.#largest.set(k, Math.max(this.#largest.get(k) ?? v, v));
      }
    if (this.#timeline.length < MAX_SECONDS)
      this.#timeline.push({
        t: Math.round((now - this.#start) / 1000),
        frames,
        frame_ms_p50: round1(percentile(sorted, 50)),
        frame_ms_p95: round1(percentile(sorted, 95)),
        frame_ms_max: round1(sorted.at(-1) ?? null),
        ...sampled,
      });
    this.#secondFrames = [];
  }

  /** Something happened (a pause, a reconnect, an error class): a name, never free text. */
  mark(now: number, what: string): void {
    if (this.#marks.length >= MAX_MARKS || !/^[a-z][a-z0-9_:.-]{0,59}$/.test(what)) return;
    this.#marks.push({ t: Math.round((now - this.#start) / 1000), what });
  }

  /** The trace's body: its length, the timeline, the marks, and the numbers to compare. */
  finish(now: number): {
    seconds: number;
    timeline: Numbers[];
    marks: { t: number; what: string }[];
    summary: Numbers;
  } {
    const sorted = [...this.#allFrames].sort((a, b) => a - b);
    const largest = (k: string) => this.#largest.get(k) ?? null;
    const latest = (k: string) => this.#latest.get(k) ?? null;
    return {
      seconds: Math.max(this.#seconds, Math.round((now - this.#start) / 1000)),
      timeline: this.#timeline,
      marks: this.#marks,
      summary: {
        frames: sorted.length,
        frame_ms_p50: round1(percentile(sorted, 50)),
        frame_ms_p95: round1(percentile(sorted, 95)),
        frame_ms_p99: round1(percentile(sorted, 99)),
        pct_seconds_under_20fps:
          this.#seconds > 0 ? round1((100 * this.#under20) / this.#seconds) : null,
        pct_seconds_under_30fps:
          this.#seconds > 0 ? round1((100 * this.#under30) / this.#seconds) : null,
        long_tasks: latest("long_tasks_total"),
        long_task_ms: latest("long_task_ms_total"),
        rtt_p95_max_ms: largest("input_rtt_p95_ms"),
        snapshot_gap_p95_max_ms: largest("snapshot_gap_p95_ms"),
        memory_mb_max: largest("memory_mb"),
        freezes: latest("freezes_total"),
        corrections: latest("corrections_total"),
        marks: this.#marks.length,
      },
    };
  }
}
